import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, Checkbox, ConfirmDialog, CopyId, ErrorState, Field, Input, Modal, Textarea } from '@bb/ui';
import { ApiError, type DeeplinkInput, type DeeplinkRouteView, type DeeplinkValidation } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? ((error.problem.issues as Array<{ path: string; message: string }> | undefined)?.map((i) => `${i.path}: ${i.message}`).join('; ') || error.message) : 'Something went wrong.');
const platforms = (r: DeeplinkRouteView) =>
  (['android', 'ios'] as const)
    .filter((p) => r.platforms[p])
    .map((p) => `${p === 'ios' ? 'iOS' : 'Android'}${r.platforms[p]?.minVersion ? ` ≥ ${r.platforms[p]!.minVersion}` : ''}`)
    .join(', ');

function ValidationResult({ result }: { result: DeeplinkValidation }) {
  if (result.kind === 'web') return <p role="status">A web link: it opens in a browser, not through the app routes.</p>;
  return (
    <div role="status">
      <p className={result.ok ? 'hint' : 'field-error'}>{result.ok ? (result.issues.length ? 'Opens, with warnings:' : 'Opens in the apps.') : 'Does not open as written:'}</p>
      {result.issues.length > 0 && (
        <ul>
          {result.issues.map((i, n) => (
            <li key={n} className={i.severity === 'error' ? 'field-error' : 'hint'}>
              {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type Draft = { scheme: string; host: string; title: string; description: string; auth: DeeplinkRouteView['auth']; android: boolean; androidMin: string; ios: boolean; iosMin: string; deprecated: boolean; params: string };
const toDraft = (r?: DeeplinkRouteView): Draft => ({
  scheme: r?.scheme ?? 'vcgmarkets',
  host: r?.host ?? '',
  title: r?.title ?? '',
  description: r?.description ?? '',
  auth: r?.auth ?? 'any',
  android: Boolean(r ? r.platforms.android : true),
  androidMin: r?.platforms.android?.minVersion ?? '',
  ios: Boolean(r ? r.platforms.ios : true),
  iosMin: r?.platforms.ios?.minVersion ?? '',
  deprecated: r?.deprecated ?? false,
  params: JSON.stringify(r?.params ?? [], null, 2),
});

/**
 * Settings → Deep links (BB-127): the app screens messages may open, with parameters, sign-in requirement and
 * platforms; a checker for links in content and a link builder. App managers maintain the routes.
 */
export function DeeplinksPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/deeplinks' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/deeplinks' });
  const queryClient = useQueryClient();
  const canManage = usePermissions().has('apps.manage');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const [platform, setPlatform] = useState<'' | 'android' | 'ios' | 'both'>('');
  const [showDeprecated, setShowDeprecated] = useState(false);
  const filters = { ...(query ? { query } : {}), ...(platform ? { platform } : {}), includeDeprecated: showDeprecated };
  const list = useQuery({ queryKey: ['deeplinks', environmentId, filters], queryFn: () => api.listDeeplinks(environmentId, filters) });
  const all = useQuery({ queryKey: ['deeplinks', environmentId, 'all'], queryFn: () => api.listDeeplinks(environmentId, {}) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['deeplinks', environmentId] });

  // Checker
  const [uri, setUri] = useState('');
  const [checkPlatform, setCheckPlatform] = useState<'' | 'android' | 'ios'>('');
  const check = useMutation({ mutationFn: () => api.validateDeeplink(environmentId, uri.trim(), checkPlatform || undefined) });

  // Builder
  const [routeId, setRouteId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const route = all.data?.items.find((r) => r.id === routeId);
  const build = useMutation({ mutationFn: () => api.buildDeeplink(environmentId, routeId, values) });

  // Editing
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [removing, setRemoving] = useState<DeeplinkRouteView | null>(null);
  const save = useMutation({
    mutationFn: async ({ id, draft }: { id: string | null; draft: Draft }) => {
      let params: DeeplinkInput['params'];
      try {
        params = JSON.parse(draft.params) as DeeplinkInput['params'];
      } catch {
        throw new ApiError(400, 'invalid_params', 'Parameters must be a JSON list');
      }
      const body = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        auth: draft.auth,
        deprecated: draft.deprecated,
        params,
        platforms: { ...(draft.android ? { android: draft.androidMin ? { minVersion: draft.androidMin } : {} } : {}), ...(draft.ios ? { ios: draft.iosMin ? { minVersion: draft.iosMin } : {} } : {}) },
      };
      return id ? api.updateDeeplink(environmentId, id, body) : api.createDeeplink(environmentId, { ...body, scheme: draft.scheme.trim(), host: draft.host.trim(), owner: 'mobile' });
    },
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteDeeplink(environmentId, id), onSuccess: async () => (setRemoving(null), await refresh()) });
  const edit = (patch: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));

  return (
    <section>
      <h1>Deep links</h1>
      <p className="hint">App screens a message can open. Links in content are checked against this list; routes that work on one platform only are marked.</p>
      <div className="filters">
        <input aria-label="Search deep links" placeholder="Search by host or title" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value as typeof platform)}>
          <option value="">All platforms</option>
          <option value="android">Android</option>
          <option value="ios">iOS</option>
          <option value="both">Android and iOS</option>
        </select>
        <Checkbox label="Show deprecated" checked={showDeprecated} onCheckedChange={setShowDeprecated} />
        {canManage && (
          <Button variant="primary" onClick={() => setEditing({ id: null, draft: toDraft() })}>
            Add route
          </Button>
        )}
      </div>
      {list.isError ? (
        <ErrorState title="Deep links could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <p aria-busy="true">Loading deep links…</p>
      ) : list.data.items.length === 0 ? (
        <p className="empty-state">No routes match.</p>
      ) : (
        <table className="table" aria-label="Deep links">
          <thead>
            <tr>
              <th scope="col">Link</th>
              <th scope="col">Screen</th>
              <th scope="col">Parameters</th>
              <th scope="col">Sign-in</th>
              <th scope="col">Platforms</th>
              {canManage && (
                <th scope="col">
                  <span className="bb-visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((r) => (
              <tr key={r.id}>
                <td>
                  <code>
                    {r.scheme}://{r.host}
                  </code>
                  {r.deprecated && <span className="tag tag--neutral"> Deprecated</span>}
                </td>
                <td>
                  {r.title}
                  {r.description && <div className="hint">{r.description}</div>}
                </td>
                <td>{r.params.map((p) => `${p.name}${p.required ? '*' : ''}: ${p.type === 'enum' ? (p.enumValues ?? []).join(' | ') : p.type}`).join(', ') || '—'}</td>
                <td>{r.auth === 'authorized' ? 'Signed in' : r.auth === 'unauthorized' ? 'Signed out' : 'Any'}</td>
                <td>{platforms(r)}</td>
                {canManage && (
                  <td>
                    <Button variant="ghost" aria-label={`Edit ${r.host}`} onClick={() => setEditing({ id: r.id, draft: toDraft(r) })}>
                      Edit
                    </Button>
                    <Button variant="ghost" aria-label={`Delete ${r.host}`} onClick={() => setRemoving(r)}>
                      Delete
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section aria-label="Check a link" className="panel">
        <h2>Check a link</h2>
        <div className="filters">
          <input aria-label="Link to check" placeholder="vcgmarkets://signal_details?id=…&symbol=…" value={uri} onChange={(e) => setUri(e.target.value)} />
          <select aria-label="Message platform" value={checkPlatform} onChange={(e) => setCheckPlatform(e.target.value as typeof checkPlatform)}>
            <option value="">Android and iOS</option>
            <option value="android">Android only</option>
            <option value="ios">iOS only</option>
          </select>
          <Button disabled={!uri.trim()} loading={check.isPending} onClick={() => check.mutate()}>
            Check
          </Button>
        </div>
        {check.data && <ValidationResult result={check.data} />}
      </section>

      <section aria-label="Build a link" className="panel">
        <h2>Build a link</h2>
        <select
          aria-label="Screen"
          value={routeId}
          onChange={(e) => {
            setRouteId(e.target.value);
            setValues({});
            build.reset();
          }}
        >
          <option value="">Choose a screen</option>
          {(all.data?.items ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.title} ({r.host})
            </option>
          ))}
        </select>
        {route && (
          <div className="filters">
            {route.params.map((p) =>
              p.type === 'enum' ? (
                <select key={p.name} aria-label={p.name} value={values[p.name] ?? ''} onChange={(e) => setValues({ ...values, [p.name]: e.target.value })}>
                  <option value="">{p.name}</option>
                  {(p.enumValues ?? []).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input key={p.name} aria-label={p.name} placeholder={`${p.name}${p.required ? ' *' : ''}${p.example ? ` (e.g. ${p.example})` : ''}`} value={values[p.name] ?? ''} onChange={(e) => setValues({ ...values, [p.name]: e.target.value })} />
              ),
            )}
            <Button onClick={() => build.mutate()} loading={build.isPending}>
              Build
            </Button>
          </div>
        )}
        {build.data && (
          <>
            <CopyId label="Link" value={build.data.uri} />
            <ValidationResult result={build.data.validation} />
          </>
        )}
      </section>

      <Modal
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editing?.id ? `Edit ${editing.draft.host}` : 'Add route'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button variant="primary" loading={save.isPending} disabled={!editing?.draft.host.trim() || !editing?.draft.title.trim()} onClick={() => editing && save.mutate(editing)}>
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <>
            {!editing.id && (
              <>
                <Field label="Scheme">
                  <Input value={editing.draft.scheme} onChange={(e) => edit({ scheme: e.target.value })} />
                </Field>
                <Field label="Host">
                  <Input value={editing.draft.host} onChange={(e) => edit({ host: e.target.value })} />
                </Field>
              </>
            )}
            <Field label="Title">
              <Input value={editing.draft.title} onChange={(e) => edit({ title: e.target.value })} />
            </Field>
            <Field label="Description">
              <Textarea value={editing.draft.description} onChange={(e) => edit({ description: e.target.value })} />
            </Field>
            <Field label="Sign-in">
              <select value={editing.draft.auth} onChange={(e) => edit({ auth: e.target.value as Draft['auth'] })}>
                <option value="any">Any</option>
                <option value="authorized">Signed in</option>
                <option value="unauthorized">Signed out</option>
              </select>
            </Field>
            <Checkbox label="Android" checked={editing.draft.android} onCheckedChange={(on) => edit({ android: on })} />
            {editing.draft.android && (
              <Field label="Android min version">
                <Input value={editing.draft.androidMin} onChange={(e) => edit({ androidMin: e.target.value })} />
              </Field>
            )}
            <Checkbox label="iOS" checked={editing.draft.ios} onCheckedChange={(on) => edit({ ios: on })} />
            {editing.draft.ios && (
              <Field label="iOS min version">
                <Input value={editing.draft.iosMin} onChange={(e) => edit({ iosMin: e.target.value })} />
              </Field>
            )}
            <Field label="Parameters (JSON)" hint='[{ "name": "id", "type": "int", "required": true }]'>
              <Textarea value={editing.draft.params} spellCheck={false} onChange={(e) => edit({ params: e.target.value })} />
            </Field>
            <Checkbox label="Deprecated" checked={editing.draft.deprecated} onCheckedChange={(on) => edit({ deprecated: on })} />
            {save.isError && <p role="alert">{errorText(save.error)}</p>}
          </>
        )}
      </Modal>
      <ConfirmDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)} title={`Delete ${removing?.host ?? ''}?`} confirmLabel="Delete" busy={remove.isPending} onConfirm={() => removing && remove.mutate(removing.id)}>
        Messages that link to it stop passing the link check. Deprecate the route instead if links may still exist.
      </ConfirmDialog>
    </section>
  );
}
